import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { manageTags } from '../../lib/api';
import { Tag, Plus, Edit, Trash2, Save, X, Search } from 'lucide-react';
import toast from 'react-hot-toast';

interface TagData {
  id: string;
  name: string;
  type: 'source' | 'topic' | 'thinking_type';
  color: string;
  created_at: string;
}

interface TagFormData {
  name: string;
  type: 'source' | 'topic' | 'thinking_type';
  color: string;
}

const TagManagement: React.FC = () => {
  const [tags, setTags] = useState<TagData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState<TagData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [formData, setFormData] = useState<TagFormData>({
    name: '',
    type: 'source',
    color: '#3B82F6',
  });

  const tagTypes = [
    { value: 'source', label: '来源标签', color: 'bg-blue-100 text-blue-800' },
    { value: 'topic', label: '主题标签', color: 'bg-green-100 text-green-800' },
    { value: 'thinking_type', label: '思维类型标签', color: 'bg-purple-100 text-purple-800' },
  ];

  const predefinedColors = [
    '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444',
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
  ];

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await manageTags('list');
      
      if (response.success) {
        setTags(response.data?.tags || []);
      } else {
        throw new Error(response.error || '获取标签失败');
      }
    } catch (error) {
      console.error('获取标签失败:', error);
      toast.error('获取标签失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('请输入标签名称');
      return;
    }

    try {
      if (editingTag) {
        // 更新标签
        const response = await manageTags('update', {
          id: editingTag.id,
          name: formData.name.trim(),
          type: formData.type,
          color: formData.color,
        });

        if (!response.success) {
          throw new Error(response.error || '更新标签失败');
        }
        toast.success('标签更新成功！');
      } else {
        // 创建新标签
        const response = await manageTags('create', {
          name: formData.name.trim(),
          type: formData.type,
          color: formData.color,
        });

        if (!response.success) {
          throw new Error(response.error || '创建标签失败');
        }
        toast.success('标签创建成功！');
      }

      resetForm();
      fetchTags();
    } catch (error: any) {
      console.error('保存标签失败:', error);
      if (error.message.includes('duplicate')) {
        toast.error('标签名称已存在');
      } else {
        toast.error('保存标签失败');
      }
    }
  };

  const handleEdit = (tag: TagData) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      type: tag.type,
      color: tag.color,
    });
    setShowForm(true);
  };

  const handleDelete = async (tagId: string) => {
    if (!confirm('确定要删除这个标签吗？')) return;

    try {
      const response = await manageTags('delete', { id: tagId });
      
      if (!response.success) {
        throw new Error(response.error || '删除标签失败');
      }
      
      toast.success('标签删除成功！');
      fetchTags();
    } catch (error) {
      console.error('删除标签失败:', error);
      toast.error('删除标签失败');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'source',
      color: '#3B82F6',
    });
    setEditingTag(null);
    setShowForm(false);
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = tag.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === '' || tag.type === selectedType;
    return matchesSearch && matchesType;
  });

  const getTypeLabel = (type: string) => {
    return tagTypes.find(t => t.value === type)?.label || type;
  };

  const getTypeColor = (type: string) => {
    return tagTypes.find(t => t.value === type)?.color || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索标签..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">所有类型</option>
              {tagTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            新建标签
          </button>
        </div>

        {/* Tag Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {editingTag ? '编辑标签' : '新建标签'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600"
                  >
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      标签名称
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入标签名称"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      标签类型
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {tagTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      标签颜色
                    </label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {predefinedColors.map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData({ ...formData, color })}
                          className={`w-8 h-8 rounded-full border-2 ${
                            formData.color === color ? 'border-gray-900' : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {editingTag ? '更新' : '创建'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Tags Grid */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              标签列表 ({filteredTags.length})
            </h2>
          </div>

          {filteredTags.length > 0 ? (
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTags.map((tag) => (
                  <div
                    key={tag.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 rounded-full mr-2"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="font-medium text-gray-900">{tag.name}</span>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEdit(tag)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        >
                        </button>
                        <button
                          onClick={() => handleDelete(tag.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${getTypeColor(tag.type)}`}>
                        {getTypeLabel(tag.type)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(tag.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-12 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm || selectedType ? '没有找到匹配的标签' : '暂无标签'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedType ? '尝试调整搜索条件' : '开始创建第一个标签'}
              </p>
              {!searchTerm && !selectedType && (
                <button
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  创建标签
                </button>
              )}
            </div>
          )}
        </div>
      </div>
  );
};

export default TagManagement;